/**
 * Contract for the hiring jobs endpoints. Shared between the route
 * handlers in products/hiring/app/api/jobs/** and the client blocks /
 * pages that hit them via apiCall/useApi.
 */
import { z } from "zod";
import type { Args, Operation } from "../types";
import { scoped } from "../types";
import {
  idNumericParams,
  paginatedSchema,
  paginationQuery,
  type PaginatedResult,
} from "./_common";

export const jobStatus = z.enum(["draft", "open", "closed", "archived"]);
export type JobStatus = z.infer<typeof jobStatus>;

export const jobSchema = z.object({
  id: z.number(),
  workspace_id: z.number(),
  slug: z.string(),
  title: z.string(),
  description: z.string(),
  department: z.string().nullable(),
  location: z.string().nullable(),
  employment_type: z.string().nullable(),
  salary_min: z.number().nullable(),
  salary_max: z.number().nullable(),
  salary_currency: z.string().nullable(),
  status: jobStatus,
  pipeline_id: z.number(),
  hiring_manager_actor_id: z.number().nullable(),
  is_public: z.number(),
  opened_at: z.string().nullable(),
  closed_at: z.string().nullable(),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type JobShape = z.infer<typeof jobSchema>;

export const jobCreateBody = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  department: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  employment_type: z.string().nullable().optional(),
  salary_min: z.number().int().nonnegative().nullable().optional(),
  salary_max: z.number().int().nonnegative().nullable().optional(),
  salary_currency: z.string().length(3).nullable().optional(),
  pipeline_id: z.number().int().optional(),
  slug: z.string().optional(),
  is_public: z.boolean().optional(),
  status: jobStatus.optional(),
});

export const jobUpdateBody = jobCreateBody.partial();

export const jobsListQuery = paginationQuery.extend({
  status: z.union([jobStatus, z.literal("all")]).optional(),
  is_public: z.enum(["true", "false"]).optional(),
  q: z.string().optional(),
});

export const jobsListResponse = paginatedSchema(jobSchema);
export type JobsListResponse = PaginatedResult<JobShape>;

export const jobsList: Operation<
  void,
  z.infer<typeof jobsListQuery>,
  void,
  z.infer<typeof jobsListResponse>
> = {
  method: "GET",
  path: () => "/hiring/api/jobs",
  query: jobsListQuery,
  response: jobsListResponse,
};

export const jobsCreate: Operation<
  void,
  void,
  z.infer<typeof jobCreateBody>,
  JobShape
> = {
  method: "POST",
  path: () => "/hiring/api/jobs",
  body: jobCreateBody,
  response: jobSchema,
  // inserts a list row; no id until result (list family is the collection).
  invalidates: [jobsList],
};

export const jobsGet: Operation<{ id: number }, void, void, JobShape> = {
  method: "GET",
  path: ({ id }) => `/hiring/api/jobs/${id}`,
  params: idNumericParams,
  response: jobSchema,
};

const scopedJobGet = scoped(
  jobsGet,
  (args: Args<{ id: number }, void, unknown>) => ({ id: args.params.id }),
);


export const slugParams = z.object({ slug: z.string().min(1) });

/**
 * Read one job by SLUG — added by RO-3-07 because the careers page is keyed on
 * the slug, not the id, and nothing served that lookup.
 *
 * `getJobBySlug()` was previously reachable only in-process: the server-rendered
 * `app/hiring/jobs/[slug]/page.tsx` called it inline while rendering. A client
 * route cannot, and `jobsList`'s `q=` is NOT a substitute — it matches
 * `title/department/location` only (`buildJobsWhere`, hiring/server/jobs.ts),
 * never `slug`, so filtering the list client-side would be both wrong and
 * paginated.
 *
 * ── Why the path is `/by-slug/<slug>` and not `/hiring/api/jobs/<slug>` ──
 *
 * Not for the ordering reason one would reach for first: `routes/index.ts`'s
 * collector sorts literal segments ahead of parameterized ones before anything
 * reaches Hono, so registration order no longer decides matches (see
 * `check-route-shadowing`'s own header).
 *
 * The reason is that a by-slug read at `/hiring/api/jobs/<slug>` is not a literal
 * at all — it is a SECOND `/hiring/api/jobs/:param`, indistinguishable from
 * `jobsGet`'s. No sort can separate two identical patterns; one of them simply
 * never runs, and which one is an implementation detail. A distinct
 * `/by-slug/:slug` segment makes the two reads different routes, which is what
 * they actually are.
 *
 * PERIMETER, deliberately: this lands under the `/hiring/api/jobs` prefix, which
 * `PUBLIC_PREFIXES` (packages/core/src/auth/route-gate.ts:27) matches with
 * `startsWith`. That is REQUIRED, not incidental — an anonymous candidate must be
 * able to read a published posting — and it exposes nothing the already-public
 * `jobsList` does not: the same `jobSchema`, for one row instead of the page.
 * The operator-only pipeline counts deliberately do NOT live here; see
 * `applicationsStageCounts` in `hiring-applications.ts` for why.
 */
export const jobsGetBySlug: Operation<{ slug: string }, void, void, JobShape> = {
  method: "GET",
  path: ({ slug }) => `/hiring/api/jobs/by-slug/${encodeURIComponent(slug)}`,
  params: slugParams,
  response: jobSchema,
};

export const jobsUpdate: Operation<
  { id: number },
  void,
  z.infer<typeof jobUpdateBody>,
  JobShape
> = {
  method: "PATCH",
  path: ({ id }) => `/hiring/api/jobs/${id}`,
  params: idNumericParams,
  body: jobUpdateBody,
  response: jobSchema,
  // title/dept/status/is_public all appear on list rows + detail.
  invalidates: [jobsList, scopedJobGet],
};

export const jobsPublish: Operation<{ id: number }, void, void, JobShape> = {
  method: "POST",
  path: ({ id }) => `/hiring/api/jobs/${id}/publish`,
  params: idNumericParams,
  response: jobSchema,
  // flips status/is_public/opened_at — list filters + detail.
  invalidates: [jobsList, scopedJobGet],
};

export const jobsClose: Operation<
  { id: number },
  void,
  { reason?: string },
  JobShape
> = {
  method: "POST",
  path: ({ id }) => `/hiring/api/jobs/${id}/close`,
  params: idNumericParams,
  body: z.object({ reason: z.string().optional() }),
  response: jobSchema,
  // flips status/closed_at — list filters + detail.
  invalidates: [jobsList, scopedJobGet],
};

export const jobsArchive: Operation<{ id: number }, void, void, { id: number }> = {
  method: "DELETE",
  path: ({ id }) => `/hiring/api/jobs/${id}`,
  params: idNumericParams,
  response: z.object({ id: z.number() }),
  // soft-archive removes from default list; detail still addressable by id.
  invalidates: [jobsList, scopedJobGet],
};

export const hiringJobs = {
  list: jobsList,
  create: jobsCreate,
  get: jobsGet,
  getBySlug: jobsGetBySlug,
  update: jobsUpdate,
  publish: jobsPublish,
  close: jobsClose,
  archive: jobsArchive,
};
